import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { AnimatePresence, motion } from "framer-motion"
import { ArrowLeft, Check } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { Controller, useForm, useWatch } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import {
	type AdminPost,
	deletePostMedia,
	POST_LIMITS,
	type PostFormData,
	PostPreview,
	type PreviewContent,
	postFormSchema,
} from "@/entities/admin-post"
import { UnsavedChangesGuard } from "@/shared/hooks/use-unsaved-changes"
import { staggerContainer, TRANSITION } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { Input } from "@/shared/ui/input"
import { PageTransition } from "@/shared/ui/page-transition"
import { RichTextEditor } from "@/shared/ui/rich-text-editor"
import { Textarea } from "@/shared/ui/textarea"
import { usePostFormTaxonomy } from "../model/use-post-form-taxonomy"
import { attachmentsOf, toMediaItems } from "../model/media-items"
import { createPost, PostSavedWithUploadError, updatePost } from "../model/save-post"
import { releaseStaged, type StagedMedia, stageFile, stagePoster } from "../model/staged-media"
import { CoverField } from "./cover-field"
import { EditorField, EditorSection } from "./editor-layout"
import { MediaManager } from "./media-manager"
import { Readiness } from "./readiness"
import { TaxonomyPicker, TaxonomyRow, TaxonomySegments } from "./taxonomy-picker"
import { Reveal } from "@/shared/ui/reveal"

const EMPTY: PostFormData = {
	title: "",
	shortDescription: "",
	description: "",
	formatIds: [],
	nicheIds: [],
	topicIds: [],
	durationIds: [],
}

function valuesOf(post: AdminPost): PostFormData {
	return {
		title: post.title ?? "",
		shortDescription: post.shortDescription ?? "",
		description: post.description ?? "",
		formatIds: post.formats?.map((item) => item.id) ?? [],
		nicheIds: post.niches?.map((item) => item.id) ?? [],
		topicIds: post.topics?.map((item) => item.id) ?? [],
		durationIds: (post.durations?.map((item) => item.id) ?? []).slice(0, 1),
	}
}

/**
 * Редактор материала: текст, файлы, карточка каталога и классификация. Где материал
 * выходит, здесь не решается: после сохранения открывается экран публикации.
 */
export function PostEditor({
	post,
	onReload,
}: {
	post?: AdminPost
	/** Перечитать пост после правок файлов, которые применяются сразу. */
	onReload?: () => Promise<unknown>
}) {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { confirm } = useConfirm()
	const [staged, setStaged] = useState<StagedMedia[]>([])
	const [cover, setCover] = useState<{ file: File; url: string } | null>(null)
	const [progress, setProgress] = useState<{
		percent: number
		message: string
	} | null>(null)
	const taxonomy = usePostFormTaxonomy()

	const form = useForm<PostFormData>({
		resolver: zodResolver(postFormSchema),
		defaultValues: post ? valuesOf(post) : EMPTY,
		mode: "onTouched",
	})
	const values = useWatch({ control: form.control }) as PostFormData

	// Поля заполняются один раз на пост: перечитывание после правки файлов не должно
	// затирать то, что уже набрано.
	const loadedFor = useRef(post?.id)
	useEffect(() => {
		if (post && loadedFor.current !== post.id) {
			loadedFor.current = post.id
			form.reset(valuesOf(post))
		}
	}, [post, form])

	// Адреса выбранных файлов освобождаются при уходе со страницы.
	const pending = useRef({ staged, cover })
	pending.current = { staged, cover }
	useEffect(
		() => () => {
			for (const item of pending.current.staged) releaseStaged(item)
			if (pending.current.cover) URL.revokeObjectURL(pending.current.cover.url)
		},
		[],
	)

	const existing = attachmentsOf(post?.media)
	const existingCover = post?.media?.find((item) => item.type === "preview") ?? null
	const coverUrl = cover?.url ?? existingCover?.url ?? null

	const save = useMutation({
		mutationFn: (data: PostFormData) => {
			const input = {
				data,
				media: staged,
				cover: cover?.file ?? null,
				existingMediaCount: existing.length,
			}
			const onProgress = (percent: number, message: string) => setProgress({ percent, message })
			return post ? updatePost(post.id, input, onProgress) : createPost(input, onProgress)
		},
		onSuccess: async (saved) => {
			await queryClient.invalidateQueries({ queryKey: ["admin-posts"] })
			await queryClient.invalidateQueries({
				queryKey: ["admin-post", saved.id],
			})
			form.reset(form.getValues())
			setStaged([])
			setCover(null)
			toast.success(post ? "Изменения сохранены" : "Черновик создан", {
				description: post ? undefined : "Опубликуйте его на нужных площадках",
			})
			navigate(`/admin/posts/${saved.id}`)
		},
		onError: async (error) => {
			if (error instanceof PostSavedWithUploadError) {
				toast.error(extractApiErrorMessage(error.uploadError, "Не все файлы загрузились"), {
					description: "Материал сохранён. Добавьте незагруженные файлы ещё раз.",
				})
				await queryClient.invalidateQueries({ queryKey: ["admin-posts"] })
				form.reset(form.getValues())
				setStaged([])
				navigate(`/admin/edit/${error.postId}`, { replace: true })
				return
			}
			toast.error(extractApiErrorMessage(error, "Не удалось сохранить"))
		},
		onSettled: () => setProgress(null),
	})

	const submit = form.handleSubmit((data) => save.mutate(data))

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
				event.preventDefault()
				if (!save.isPending) void submit()
			}
		}
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [save.isPending, submit])

	const dirty = form.formState.isDirty || staged.length > 0 || !!cover
	const errors = form.formState.errors

	const stage = async (files: File[]) => {
		const prepared = await Promise.all(files.map(stageFile))
		setStaged((current) => [...current, ...prepared])
	}

	const removeCover = () => {
		if (cover) {
			URL.revokeObjectURL(cover.url)
			setCover(null)
			return
		}
		if (!post || !existingCover) return
		confirm({
			title: "Убрать обложку?",
			message: "В каталоге приложений вместо неё будет заглушка.",
			confirmText: "Убрать",
			onConfirm: async () => {
				await deletePostMedia(post.id, existingCover.id)
				await onReload?.()
			},
		})
	}

	const formatNames = (taxonomy.formats ?? [])
		.filter((item) => values.formatIds?.includes(item.id))
		.map((item) => item.name)

	const preview: PreviewContent = {
		title: values.title ?? "",
		shortDescription: values.shortDescription ?? "",
		description: values.description ?? "",
		coverUrl,
		formats: formatNames,
		media: toMediaItems(existing, staged).map((item) => ({
			key: item.key,
			kind: item.kind,
			imageUrl: item.posterUrl,
			name: item.name,
		})),
	}

	const checks = [
		{
			label: "Заголовок",
			done: !!values.title?.trim(),
			required: true,
			target: "post-card",
		},
		{ label: "Обложка для каталога", done: !!coverUrl, target: "post-card" },
		{
			label: "Текст или файлы",
			done: !!values.description?.trim() || preview.media.length > 0,
			target: "post-text",
		},
		{
			label: "Формат",
			done: (values.formatIds?.length ?? 0) > 0,
			target: "post-taxonomy",
		},
		{
			label: "Ниша или тема",
			done: (values.nicheIds?.length ?? 0) + (values.topicIds?.length ?? 0) > 0,
			target: "post-taxonomy",
		},
	]

	const back = post ? `/admin/posts/${post.id}` : "/admin"

	return (
		<PageTransition>
			<UnsavedChangesGuard dirty={dirty && !save.isPending && !save.isSuccess} />
			<form onSubmit={submit} noValidate>
				<div className="sticky top-[53px] z-20 border-b border-border bg-background/85 backdrop-blur-md lg:top-0">
					<div className="container mx-auto flex max-w-6xl items-center gap-3 px-3 py-3 sm:px-6">
						<Button asChild variant="ghost" size="icon" className="shrink-0">
							<Link to={back} aria-label="Назад">
								<ArrowLeft className="size-5" />
							</Link>
						</Button>
						<div className="min-w-0 flex-1">
							<p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
								{post ? "Редактирование" : "Новый материал"}
							</p>
							<h1 className="truncate text-lg font-semibold">
								{values.title?.trim() || (post ? "Без названия" : "Черновик")}
							</h1>
						</div>
						<AnimatePresence>
							{dirty && !save.isPending ? (
								<motion.span
									initial={{ opacity: 0, x: 6 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0 }}
									className="hidden text-xs text-muted-foreground sm:inline"
								>
									Есть несохранённые изменения
								</motion.span>
							) : null}
						</AnimatePresence>
						<Button asChild variant="ghost" className="hidden sm:inline-flex">
							<Link to={back}>Отмена</Link>
						</Button>
						<Button type="submit" isLoading={save.isPending} className="min-w-32">
							{save.isPending ? null : <Check />}
							{post ? "Сохранить" : "Создать черновик"}
						</Button>
					</div>
					{/* Полоса загрузки файлов раскрывается по высоте: шапка растёт плавно. */}
					<Reveal show={!!progress}>
						<div className="border-t border-border">
							<div className="container mx-auto flex max-w-6xl items-center gap-3 px-3 py-2 text-xs text-muted-foreground sm:px-6">
								<div className="h-1 flex-1 overflow-hidden rounded-full bg-secondary">
									<motion.div
										className="h-full rounded-full bg-primary"
										animate={{ width: `${progress?.percent ?? 100}%` }}
										transition={TRANSITION.base}
									/>
								</div>
								<span className="max-w-[50%] truncate">{progress?.message}</span>
							</div>
						</div>
					</Reveal>
				</div>

				<div className="container mx-auto grid max-w-6xl items-start gap-6 px-3 py-6 sm:px-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:py-8">
					<motion.div
						variants={staggerContainer(0.05)}
						initial="hidden"
						animate="visible"
						className="min-w-0 space-y-5"
					>
						<EditorSection
							id="post-card"
							title="Карточка в каталоге"
							description="Так материал выглядит в списке мини-аппа и мобильного приложения."
						>
							<EditorField label="Обложка" hint="Показывается в каталоге. В канал не уходит.">
								<CoverField
									url={coverUrl}
									onPick={(file) => {
										if (cover) URL.revokeObjectURL(cover.url)
										setCover({ file, url: URL.createObjectURL(file) })
									}}
									onRemove={coverUrl ? removeCover : undefined}
								/>
							</EditorField>
							<EditorField
								label="Заголовок"
								htmlFor="post-title"
								required
								error={errors.title?.message}
								count={{
									value: values.title?.length ?? 0,
									max: POST_LIMITS.title,
								}}
							>
								<Input
									id="post-title"
									placeholder="Например: Как собрать воронку для эксперта"
									autoComplete="off"
									aria-invalid={!!errors.title}
									className="h-12 text-base font-medium"
									{...form.register("title")}
								/>
							</EditorField>
							<EditorField
								label="Короткое описание"
								htmlFor="post-short"
								hint="Две-три строки под заголовком в каталоге."
								error={errors.shortDescription?.message}
								count={{
									value: values.shortDescription?.length ?? 0,
									max: POST_LIMITS.shortDescription,
								}}
							>
								<Textarea
									id="post-short"
									rows={3}
									placeholder="О чём материал и чем полезен"
									className="resize-none [field-sizing:content]"
									{...form.register("shortDescription")}
								/>
							</EditorField>
						</EditorSection>

						<EditorSection
							id="post-text"
							title="Текст поста"
							description="Уходит сообщением в канал и открывается в приложениях под файлами."
						>
							<Controller
								control={form.control}
								name="description"
								render={({ field }) => (
									<RichTextEditor
										format="post-markdown"
										value={field.value ?? ""}
										onChange={field.onChange}
										ariaLabel="Текст поста"
										placeholder="Напишите текст. Выделите слово и нажмите ⌘B, чтобы сделать его жирным."
										minHeight="14rem"
										contentClassName="px-4 py-3 text-[15px] leading-relaxed md:text-[15px]"
										invalid={!!errors.description}
										footer={
											<span
												className={cn(
													"text-xs tabular-nums",
													(field.value?.length ?? 0) > POST_LIMITS.description
														? "text-destructive"
														: "text-muted-foreground",
												)}
											>
												{field.value?.length ?? 0}/{POST_LIMITS.description}
											</span>
										}
									/>
								)}
							/>
							{errors.description ? (
								<p role="alert" className="-mt-4 text-xs text-destructive">
									{errors.description.message}
								</p>
							) : null}
						</EditorSection>

						<EditorSection
							id="post-media"
							title="Файлы"
							description="Фото и видео уходят в канал альбомом, в порядке плиток. Нажмите на плитку, чтобы посмотреть файл или сменить обложку видео."
							aside={
								existing.length + staged.length ? (
									<span className="shrink-0 text-xs text-muted-foreground tabular-nums">
										{existing.length + staged.length}
									</span>
								) : null
							}
						>
							<MediaManager
								postId={post?.id}
								existing={existing}
								staged={staged}
								onStage={(files) => void stage(files)}
								onRemoveStaged={(key) =>
									setStaged((current) => {
										const item = current.find((entry) => entry.key === key)
										if (item) releaseStaged(item)
										return current.filter((entry) => entry.key !== key)
									})
								}
								onMoveStaged={(key, direction) =>
									setStaged((current) => {
										const next = [...current]
										const from = next.findIndex((entry) => entry.key === key)
										const to = from + direction
										if (from < 0 || to < 0 || to >= next.length) return current
										;[next[from], next[to]] = [next[to]!, next[from]!]
										return next
									})
								}
								onStagedPoster={async (key, file) => {
									const poster = await stagePoster(file)
									setStaged((current) =>
										current.map((entry) => {
											if (entry.key !== key) return entry
											if (entry.poster) URL.revokeObjectURL(entry.poster.objectUrl)
											return { ...entry, poster }
										}),
									)
									toast.success("Обложка видео выбрана", {
										description: "Загрузится при сохранении",
									})
								}}
								onExistingChanged={() => onReload?.() ?? Promise.resolve()}
							/>
						</EditorSection>

						<EditorSection
							id="post-taxonomy"
							title="Классификация"
							description="По этим меткам материал находят в фильтрах каталога."
						>
							<div>
								<TaxonomyRow label="Формат">
									<Controller
										control={form.control}
										name="formatIds"
										render={({ field }) => (
											<TaxonomyPicker
												label="Формат"
												kind="formats"
												items={taxonomy.formats}
												value={field.value}
												onChange={field.onChange}
												loading={taxonomy.isLoading}
											/>
										)}
									/>
								</TaxonomyRow>
								<TaxonomyRow label="Ниши">
									<Controller
										control={form.control}
										name="nicheIds"
										render={({ field }) => (
											<TaxonomyPicker
												label="Ниши"
												kind="niches"
												items={taxonomy.niches}
												value={field.value}
												onChange={field.onChange}
												loading={taxonomy.isLoading}
											/>
										)}
									/>
								</TaxonomyRow>
								<TaxonomyRow label="Темы">
									<Controller
										control={form.control}
										name="topicIds"
										render={({ field }) => (
											<TaxonomyPicker
												label="Темы"
												kind="topics"
												items={taxonomy.topics}
												value={field.value}
												onChange={field.onChange}
												loading={taxonomy.isLoading}
											/>
										)}
									/>
								</TaxonomyRow>
								<TaxonomyRow label="Длительность">
									<Controller
										control={form.control}
										name="durationIds"
										render={({ field }) => (
											<TaxonomySegments
												items={taxonomy.durations}
												value={field.value}
												onChange={field.onChange}
												loading={taxonomy.isLoading}
											/>
										)}
									/>
								</TaxonomyRow>
							</div>
						</EditorSection>
					</motion.div>

					<aside className="space-y-4 lg:sticky lg:top-24">
						<PostPreview content={preview} />
						<Readiness checks={checks} />
					</aside>
				</div>
			</form>
		</PageTransition>
	)
}
